import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { keyframes } from '@emotion/react';
import { PRIMARY_COLORS, SECONDARY_COLORS, TERTIARY_COLORS } from '../../common/colors';

// OpenFn-style "wall of love": a vertical auto-scrolling ticker of quote
// cards. The real testimonials come from the shared, page-neutral
// `testimonials.*` namespace — the same place toolsConfig.js reads them for
// the per-tool Testimonial — so each quote is defined once and neither
// surface owns the other's content. They used to be read straight out of
// tools_config.*.testimonial; when /tools/mapper traded its testimonial for a
// product showcase (ocl_online#129) that key vanished and took the whole home
// page down with it (ocl_online#182). Only real quotes with recorded
// permission run here: the two fictional placeholder cards this design
// shipped with, and Beatriz de Faria Leão's quote, were removed
// (ocl_online#239). Permission for each quote is tracked in ocl-strategy's
// docs/plans/community-site-launch-review/16-testimonial-provenance.md.
const AVATAR_COLORS = [PRIMARY_COLORS['40'], TERTIARY_COLORS['40'], SECONDARY_COLORS['40']];
const CARD_SECONDS = 6;
// With fewer cards, one copy of the list can be shorter than the window, and
// the loop shows empty space before it wraps. Four cards clear the 520px
// desktop window even when every quote is one line; below that the cards
// stack without scrolling, as they already do under reduced motion
// (ocl_online#239).
const MIN_SCROLLING_CARDS = 4;
const STATIC_WINDOW = { height: 'auto', overflow: 'visible', maskImage: 'none', WebkitMaskImage: 'none' };

const scroll = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
`;

const initials = name => name.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase();

// `avatar` is optional and only set for people who have given us their photo;
// everyone else keeps the initials-on-brand-color fallback. MUI's Avatar falls
// back to the children on its own if the src is missing or fails to load, and
// the photo is decorative (alt="") because the name sits right beside it.
const TestimonialCard = ({ quote, name, role, color, avatar }) => (
  <Box sx={{ backgroundColor: 'primary.contrastText', borderRadius: 2, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)', p: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Avatar src={avatar || undefined} alt="" sx={{ bgcolor: color, width: 40, height: 40, fontSize: '0.875rem' }}>{initials(name)}</Avatar>
      <Box>
        <Typography sx={{ color: 'surface.dark', fontWeight: 700, fontSize: '0.875rem' }}>{name}</Typography>
        <Typography variant="caption" sx={{ color: 'surface.contrastText' }}>{role}</Typography>
      </Box>
    </Box>
    <Typography
      variant="body2"
      sx={{ color: 'surface.dark', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      “{quote}”
    </Typography>
  </Box>
);

const Testimonials = () => {
  const { t } = useTranslation();
  // Two independent reasons the ticker can be paused: transient hover/focus,
  // and the explicit toggle button (which must persist even after the
  // pointer leaves, since reaching the button requires hovering over it).
  const [hovered, setHovered] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const paused = hovered || manuallyPaused;

  const items = [
    t('testimonials.ocl_online', { returnObjects: true }),
    t('testimonials.mapper', { returnObjects: true }),
    t('testimonials.madiro', { returnObjects: true }),
  ]
    // i18next returns the key *string* for a key that isn't there, which
    // spreads into a char map and throws in initials(). Drop anything that
    // isn't a well-formed testimonial, so a content mistake costs one card
    // instead of the entire page render (ocl_online#182).
    .filter(item => item && item.quote && item.name)
    .map((item, i) => ({ ...item, color: AVATAR_COLORS[i % AVATAR_COLORS.length] }));
  const scrolls = items.length >= MIN_SCROLLING_CARDS;
  // Doubled so the loop can wrap seamlessly; the second copy is hidden from
  // assistive tech (and, under reduced motion, from everyone) so each
  // testimonial is only encountered once.
  const track = scrolls ? [...items, ...items] : items;

  return (
    <Box component="section" id="testimonials" aria-labelledby="testimonials-heading" sx={{ backgroundColor: 'surface.n96' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 6 }, alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {t('home.testimonials.eyebrow')}
            </Typography>
            <Typography id="testimonials-heading" variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 2 }}>
              {t('home.testimonials.heading')}
            </Typography>
            <Typography sx={{ color: 'surface.contrastText' }}>
              {t('home.testimonials.text')}
            </Typography>
          </Box>
          <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            sx={scrolls ? {
              position: 'relative',
              height: { xs: 420, md: 520 },
              overflow: 'hidden',
              maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
              '@media (prefers-reduced-motion: reduce)': STATIC_WINDOW,
            } : STATIC_WINDOW}>
            {scrolls && <IconButton
              onClick={() => setManuallyPaused(p => !p)}
              aria-label={t(manuallyPaused ? 'home.testimonials.play' : 'home.testimonials.pause')}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'primary.contrastText',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                '&:hover': { backgroundColor: 'primary.contrastText' },
              }}>
              {manuallyPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>}
            {/* The space between cards is each card's bottom padding, not a flex
                gap: a gap only sits between cards, so the -50% scroll stopped
                half a gap short of one full copy and the loop jumped 12px at
                every wrap. With padding, one copy is exactly half the track. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                animation: scrolls ? `${scroll} ${items.length * CARD_SECONDS}s linear infinite` : 'none',
                animationPlayState: paused ? 'paused' : 'running',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}>
              {track.map((item, i) => (
                <Box key={i} aria-hidden={i >= items.length} data-duplicate={i >= items.length || undefined}
                  sx={{ pb: 3, '@media (prefers-reduced-motion: reduce)': { '&[data-duplicate]': { display: 'none' } } }}>
                  <TestimonialCard {...item} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;
